import { normalizeRegulationCountryCodes } from '../utils/regulationCountries';

const COOKIE_OPTIONS = {
  path: '/',
  sameSite: 'lax' as const,
};

const PALEN_BOT_PROFILE_COOKIE = 'palenBotProfile';
const PALEN_APP_THEME_COOKIE = 'palenAppTheme';
const PALEN_REGULATION_COUNTRIES_COOKIE = 'palenRegulationCountries';
const REGULATION_COUNTRIES_EXPIRATION_DAYS = 2 / 24;

export class CookieService {
  static set(name: string, value: string, days?: number): void {
    if (typeof document === 'undefined') {
      return;
    }

    let expires = '';
    if (days) {
      const date = new Date();
      date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
      expires = `; expires=${date.toUTCString()}`;
    } else {
      const date = new Date();
      date.setTime(date.getTime() + 365 * 24 * 60 * 60 * 1000);
      expires = `; expires=${date.toUTCString()}`;
    }

    const secure = this.shouldUseSecureCookie() ? '; secure' : '';
    const sameSite = `; samesite=${COOKIE_OPTIONS.sameSite}`;

    document.cookie = `${name}=${value}${expires}${secure}${sameSite}; path=${COOKIE_OPTIONS.path}`;
  }

  static get(name: string): string | null {
    if (typeof document === 'undefined') {
      return null;
    }

    const nameEQ = `${name}=`;
    const ca = document.cookie.split(';');

    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }

    return null;
  }

  static remove(name: string): void {
    if (typeof document === 'undefined') {
      return;
    }

    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${COOKIE_OPTIONS.path}`;
  }

  static getPalenBotProfileCode(): string | null {
    return this.get(PALEN_BOT_PROFILE_COOKIE);
  }

  static setPalenBotProfileCode(code: string, expiresInDays?: number): void {
    this.set(PALEN_BOT_PROFILE_COOKIE, code, expiresInDays);
  }

  static getAppTheme(): string | null {
    return this.get(PALEN_APP_THEME_COOKIE);
  }

  static setAppTheme(theme: string, expiresInDays?: number): void {
    this.set(PALEN_APP_THEME_COOKIE, theme, expiresInDays);
  }

  static getRegulationCountryCodes(): string[] | null {
    const value = this.get(PALEN_REGULATION_COUNTRIES_COOKIE);
    if (!value) {
      return null;
    }

    try {
      const parsed = JSON.parse(decodeURIComponent(value)) as unknown;
      if (!Array.isArray(parsed)) {
        return null;
      }

      const countryCodes = normalizeRegulationCountryCodes(
        parsed.map((item) => String(item || '')),
      );
      return countryCodes.length ? countryCodes : null;
    } catch {
      return null;
    }
  }

  static setRegulationCountryCodes(countryCodes: string[]): void {
    const normalizedCountryCodes = normalizeRegulationCountryCodes(countryCodes);
    if (!normalizedCountryCodes.length) {
      this.remove(PALEN_REGULATION_COUNTRIES_COOKIE);
      return;
    }

    this.set(
      PALEN_REGULATION_COUNTRIES_COOKIE,
      encodeURIComponent(JSON.stringify(normalizedCountryCodes)),
      REGULATION_COUNTRIES_EXPIRATION_DAYS,
    );
  }

  private static shouldUseSecureCookie(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }

    return window.location.protocol === 'https:';
  }
}
